/**
 * Loose shape of the error objects thrown by the API client and React Query.
 * Use with a cast in catch blocks (`const err = error as ErrorLike`) to read
 * status/message fields without resorting to `any`.
 */
export interface ErrorLike {
  status?: number;
  message?: string;
  response?: { status?: number; data?: { message?: string } };
}

/** Normalized HTTP/API errors thrown by ApiClient. */
export class ApiHttpError extends Error {
  status: number;
  response?: { status: number; data?: unknown };

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = status;
    this.response = { status, data };
  }
}

export function isApiHttpError(error: unknown): error is ApiHttpError {
  return error instanceof ApiHttpError;
}

export function getApiErrorStatus(error: unknown): number | undefined {
  if (isApiHttpError(error)) {
    return error.status;
  }
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { status?: unknown } }).response;
    return typeof response?.status === 'number' ? response.status : undefined;
  }
  return undefined;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as { response?: { data?: { message?: string } } }).response?.data;
    return data?.message || fallback;
  }
  return fallback;
}

export function isUnauthorizedError(error: unknown): boolean {
  const status = getApiErrorStatus(error);
  return status === 401;
}
